import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Page } from "./types";
import ScrollToTop from "./ScrollToTop";
import ErrorBoundary from "designs/Common/ErrorBoundary";
import Footer from "shared/Footer/Footer";
import Page404 from "containers/Page404/Page404";
import ListingCarPage from "containers/ListingCarPage/ListingCarPage";
import CheckOutPage from "containers/CheckOutPage/CheckOutPage";
import PayPage from "containers/PayPage/PayPage";
import AccountPage from "containers/AccountPage/AccountPage";
import AccountPass from "containers/AccountPage/AccountPass";
import AccountSavelists from "containers/AccountPage/AccountSavelists";
import AccountBilling from "containers/AccountPage/AccountBilling";
import PageContact from "containers/PageContact/PageContact";
import PageAbout from "containers/PageAbout/PageAbout";
import PageSignUp from "containers/PageSignUp/PageSignUp";
import PageLogin from "containers/PageLogin/PageLogin";
import PageForgotPass from "containers/PageForgotPass/PageForgotPass";
import SiteHeader from "containers/SiteHeader";
import FooterNav from "components/FooterNav";
import useWindowSize from "hooks/useWindowResize";
import PageHome3 from "containers/PageHome/PageHome3";
import ListingCarDetailPage from "containers/ListingDetailPage/listing-car-detail/ListingCarDetailPage";
import AvailableCarsPage from "containers/AvailableCarsPage/AvailableCarsPage";
import ComingSoon from "components/ComingSoon";
import Requirements from "containers/RequirementPage/Requirements";
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import PageResetPass from "containers/PageResetPass/PageResetPass";
import PageSetPass from "containers/PageSetPass/PageSetPass";
import { useData } from "data/data-provider";
import BookingDetailsPage from "containers/BookingDetailsPage/BookingDetailsPage";
import CarDetail from "containers/CarDetail/CarDetail";
import CarListingCarDetailPage from "containers/CarListingDetailPage/CarListingDetailPage";
import TermsConditions from "components/Terms&Conditions/TermsConditions";
import PrivacyPolicy from "components/PrivacyPolicy/PrivacyPolicyPage";
import PayLaterPoliciesPage from "designs/Common/PayLaterPoliciesPage/PayLaterPoliciesPage";
import PaymentSuccess from "designs/Common/PaymentSuccess/PaymentSuccess";
import PaymentLinkRedirect from "designs/Common/PaymentLinkRedirect/PaymentLinkRedirect";
import RouteHistoryTracker from "./RouteHistoryTracker";
import PostHogPageview from "analytics/PostHogPageview";

export const pages: Page[] = [
  { path: "/", exact: true, component: PageHome3 },
  { path: "/home-3", component: PageHome3 },

  //
  { path: "/available-car", component: AvailableCarsPage },
  { path: "/vehicles", component: ListingCarPage },
  { path: "/booking-details", component: BookingDetailsPage },
  { path: "/listing-car-detail", component: ListingCarDetailPage },

  //
  { path: "/checkout", component: CheckOutPage },
  { path: "/payment-success", component: PaymentSuccess },
  { path: "/pay-later-policies", component: PayLaterPoliciesPage },
  { path: "/pay-done/:bookingId", component: PayPage },
  //
  { path: "/account", component: AccountPage },
  { path: "/account-password", component: AccountPass },
  { path: "/account-bookings", component: AccountSavelists },
  { path: "/resetpassword/:token", component: PageResetPass },
  { path: "/setpassword/:token", component: PageSetPass },
  { path: "/account-billing", component: AccountBilling },
  // 1NOW-10372: publicly linkable legal pages, reachable without opening
  // the checkout modal (needed for SMS-carrier campaign approval).
  { path: "/privacy-policy", component: PrivacyPolicy },
  { path: "/terms-conditions", component: TermsConditions },

  //
  { path: "/contact", component: PageContact },
  { path: "/about", component: PageAbout },
  { path: "/coming-soon", component: ComingSoon },
  { path: "/requirements", component: Requirements },

  { path: "/car-listing-detail", component: CarListingCarDetailPage },
];
export enum AuthPages {
  Login,
  Signup,
  ForgotPass,
}
const MyRoutes = () => {
  let { isAuthPanelOpen, setIsAuthPanelOpen } = useData();
  let [authPageName, setAuthPageName] = useState<AuthPages>(AuthPages.Login);
  let WIN_WIDTH = useWindowSize().width;
  if (typeof window !== "undefined") {
    WIN_WIDTH = WIN_WIDTH || window.innerWidth;
  }

  useEffect(() => {
    if (isAuthPanelOpen) {
      setAuthPageName(AuthPages.Login);
    }
  }, [isAuthPanelOpen]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteHistoryTracker />
      <PostHogPageview />
      <AppShell
        isAuthPanelOpen={isAuthPanelOpen}
        setIsAuthPanelOpen={setIsAuthPanelOpen}
        authPageName={authPageName}
        setAuthPageName={setAuthPageName}
        WIN_WIDTH={WIN_WIDTH}
      />
    </BrowserRouter>
  );
};

interface AppShellProps {
  isAuthPanelOpen: boolean;
  setIsAuthPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  authPageName: AuthPages;
  setAuthPageName: React.Dispatch<React.SetStateAction<AuthPages>>;
  WIN_WIDTH: number;
}

// Payment short-link redirect (`/pay/:slug`, SMS -> Stripe/Square, 1NOW-1530)
// bypasses the whole rental-site shell -- no SiteHeader, no Footer, no
// waiting on the rest of the app -- so the renter only ever sees the
// redirect splash for the ~150-400ms round-trip before it bounces them
// straight to the card page. Mirrors how Template intercepts this path
// before its normal route tree renders.
const AppShell: React.FC<AppShellProps> = ({
  isAuthPanelOpen,
  setIsAuthPanelOpen,
  authPageName,
  setAuthPageName,
  WIN_WIDTH,
}) => {
  const location = useLocation();
  if (location.pathname.startsWith("/pay/")) {
    return <PaymentLinkRedirect />;
  }

  return (
    <>
      <SiteHeader />

      {/* 1NOW-1642: header and footer stay outside the boundary so a failed
          page still leaves the renter a way to navigate. */}
      <ErrorBoundary name="routes">
        <Routes>
          {pages.map(({ component, path }) => {
            const Component = component;
            return <Route key={path} element={<Component />} path={path} />;
          })}
          <Route path="*" element={<Page404 />} />
        </Routes>
      </ErrorBoundary>

      {WIN_WIDTH < 768 && <FooterNav />}
      <Footer />
      <Transition appear show={isAuthPanelOpen}>
        <Dialog
          as="div"
          className="relative z-10 focus:outline-none"
          onClose={() => {
            setIsAuthPanelOpen(false);
          }}
        >
          <div className="fixed inset-0 z-1000 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 transform-[scale(95%)]"
                enterTo="opacity-100 transform-[scale(100%)]"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 transform-[scale(100%)]"
                leaveTo="opacity-0 transform-[scale(95%)]"
              >
                <DialogPanel className="w-full max-w-md lg:h-[700px] lg:max-w-2xl bg-[#ffffffff] rounded-xl p-6 backdrop:blur-2xl border border-text-color shadow-2xl">
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setIsAuthPanelOpen(false);
                      }}
                      className="text-text-color"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  {authPageName === AuthPages.Login ? (
                    <PageLogin setAuthPageName={setAuthPageName} />
                  ) : authPageName === AuthPages.ForgotPass ? (
                    <PageForgotPass setAuthPageName={setAuthPageName} />
                  ) : (
                    <PageSignUp setAuthPageName={setAuthPageName} />
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default MyRoutes;
